import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, Eye, Heart, Waves } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { services } from "../data/services";
import ServiceCard from "../components/ServiceCard";
import { CONTACT } from "../lib/api";

const values = [
  {
    icon: Eye,
    title: "Empower through awareness",
    body: "We begin with mindfulness — helping people connect deeply with themselves. Through reflection and self-awareness, participants build emotional intelligence, confidence and clarity.",
  },
  {
    icon: Users,
    title: "Small team, big heart",
    body: "You'll get to know every one of us by name. No call centres, no strangers turning up on your doorstep.",
  },
  {
    icon: Heart,
    title: "Nurture the whole person",
    body: "Our supports are holistic — integrating mind, body and spirit. We encourage balanced living through healthy food, movement, creativity and quiet moments.",
  },
];

const faqs = [
  {
    q: "What is the NDIS?",
    a: "The National Disability Insurance Scheme (NDIS) is an Australian government scheme that provides funding to eligible people to help them access the supports and services they need to live their best life.",
  },
  {
    q: "Who is eligible for the NDIS?",
    a: "You may be eligible if you are aged under 65, an Australian citizen or permanent resident, and have a permanent disability that significantly affects your ability to take part in everyday activities. The NDIS website has a full eligibility checklist.",
  },
  {
    q: "What is capacity building?",
    a: "Capacity building is NDIS funding that helps you build skills, independence and confidence over time — things like learning to cook, catching public transport, making friends or preparing for work. It sits at the heart of everything we do at PeerWave.",
  },
  {
    q: "What can NDIS funding be used for?",
    a: "Funding is used for reasonable and necessary supports linked to your goals — including therapy, personal care, community access, skill-building classes, transport, equipment and social programs like the ones PeerWave runs.",
  },
  {
    q: "Do I need to be plan-managed or self-managed to work with PeerWave?",
    a: "We work with self-managed and plan-managed participants. If you're NDIA-managed, get in touch and we'll walk you through the options.",
  },
  {
    q: "How do I get started with PeerWave?",
    a: "Give us a call on 0400 229 777, email peerwave.club@proton.me, or fill out the contact form. We'll book in a no-pressure chat to hear what you're after and figure out what fits.",
  },
  {
    q: "Where does PeerWave operate?",
    a: "We're based on the Fraser Coast in Queensland and run classes, groups and community-based supports across the region.",
  },
];

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 grain opacity-60" />
        <div className="max-w-7xl mx-auto container-px pt-16 pb-20 md:pt-24 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">
              Find your voice.
              <br />
              Find your tribe.
              <br />
              With <span className="wave-underline">PeerWave</span>.
            </h1>
            <blockquote className="mt-7 text-lg text-foreground/75 leading-relaxed border-l-2 border-primary/40 pl-5 italic max-w-xl">
              "At PeerWave, we empower individuals to uncover their unique
              gifts, grow through collaboration, and find belonging in
              community. Our mission is to inspire confidence, cultivate
              purpose, and bring joy to every step of the journey."
            </blockquote>
            <p className="mt-3 text-sm text-muted-foreground">
              — Laoise, Director &amp; Founder
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                data-testid="hero-cta-services"
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-base shadow-sm hover:-translate-y-0.5 transition-transform"
              >
                <Link to="/services">
                  Explore services
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-contact"
                className="rounded-full h-14 px-8 text-base border-border bg-card hover:bg-secondary hover:-translate-y-0.5 transition-transform"
              >
                <Link to="/contact">Talk to a human</Link>
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-muted-foreground">
              <a href={CONTACT.phoneDial} data-testid="hero-phone" className="hover:text-foreground transition">
                <span className="text-foreground/90 font-medium">{CONTACT.phone}</span>
              </a>
              <span className="hidden sm:inline text-border">|</span>
              <a href={CONTACT.emailLink} data-testid="hero-email" className="hover:text-foreground transition break-all">
                {CONTACT.email}
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-square w-full rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-black/5 animate-fade-up">
              <img
                src="https://customer-assets.emergentagent.com/job_peerwave-landing/artifacts/vw8jwcqn_girls.jpg"
                alt="Two PeerWave participants enjoying a bright day out by the pool"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-transparent" />
            </div>
            {/* floating chip */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 rounded-2xl bg-card px-5 py-4 shadow-lg ring-1 ring-black/5 animate-float">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="font-heading text-sm font-semibold">6 supported programs</p>
                <p className="text-xs text-muted-foreground">Crochet · Painting · Craft · Daily Living · Social · Work</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative bg-secondary/60 border-y border-border/60">
        <div className="max-w-7xl mx-auto container-px py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Why PeerWave</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
              The kind of NDIS provider we couldn't find. So we built it.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl bg-card p-8 border border-border/70 hover:border-primary/40 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-heading text-xl font-medium">{v.title}</h3>
                <p className="mt-3 text-foreground/70 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto container-px py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">What we do</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
              Six programs. One promise — <span className="italic text-foreground/70">you choose</span>.
            </h2>
          </div>
          <Button
            asChild
            variant="ghost"
            data-testid="home-view-all-services"
            className="rounded-full self-start md:self-auto text-foreground/80 hover:bg-secondary"
          >
            <Link to="/services">
              View all services
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {services.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto container-px pb-20 md:pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Common questions</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
            Frequently asked questions
          </h2>
          <p className="mt-5 text-lg text-foreground/70">
            Everything people usually ask before they call. Missing something? Give us a ring.
          </p>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 space-y-3"
          data-testid="faq-accordion"
        >
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="rounded-2xl border border-border/70 bg-card px-5 sm:px-6 data-[state=open]:border-primary/40 transition-colors"
            >
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="text-left font-heading text-base sm:text-lg font-medium py-5 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-answer-${i}`}
                className="text-foreground/75 text-base leading-relaxed pb-5"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto container-px pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-foreground text-background p-10 md:p-16">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/40 blur-3xl" />
          <div className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
          <div className="relative max-w-2xl">
            <p className="text-xs uppercase tracking-[0.24em] text-primary font-semibold">Ready when you are</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
              Let's have a chat — no pressure, just a cuppa.
            </h2>
            <p className="mt-5 text-background/80 text-lg leading-relaxed max-w-xl">
              Whether you're new to the NDIS or already have a plan, we'll listen first and help you figure out what fits.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                data-testid="cta-strip-contact"
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-base"
              >
                <Link to="/contact">Get in touch</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="cta-strip-phone"
                className="rounded-full h-14 px-8 text-base bg-transparent border-background/30 text-background hover:bg-background/10 hover:text-background"
              >
                <a href={CONTACT.phoneDial}>Call {CONTACT.phone}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
